import { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet } from './apiClient.js';

/**
 * Generic polling hook for API endpoints
 * @param {string} endpoint - API endpoint to poll (e.g., '/user/balance')
 * @param {number} intervalMs - Polling interval in milliseconds
 * @param {Object} options - Additional options
 * @param {boolean} options.enabled - Whether polling is enabled (default: true)
 * @param {Function} options.onSuccess - Callback on successful fetch
 * @param {Function} options.onError - Callback on error
 * @returns {Object} { data, loading, error, refetch }
 */
export const usePolling = (endpoint, intervalMs, options = {}) => {
  const {
    enabled = true,
    onSuccess,
    onError,
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  });

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    try {
      const result = await apiGet(endpoint);

      if (mountedRef.current) {
        setData(result);
        setError(null);
        setLoading(false);
        onSuccessRef.current?.(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message);
        setLoading(false);
        onErrorRef.current?.(err);
      }
    }
  }, [endpoint, enabled]);

  // Initial fetch and polling setup
  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      fetchData(); // Initial fetch

      // Set up polling interval
      intervalRef.current = setInterval(fetchData, intervalMs);
    }

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData, intervalMs, enabled]);

  // Manual refetch function
  const refetch = useCallback(() => {
    setLoading(true);
    return fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
};

/**
 * Hook to poll user balance
 * @param {boolean} isLoggedIn - Whether user is logged in
 * @param {number} chainId - Chain ID to fetch balance for
 * @returns {Object} { balance, balanceUsd, loading, error, refetch }
 */
export const useBalance = (isLoggedIn = true, chainId) => {
  const chainParam = chainId ? `?chainId=${chainId}` : '';
  const { data, loading, error, refetch } = usePolling(
    `/user/balance${chainParam}`,
    10000, // Poll every 10 seconds
    { enabled: isLoggedIn }
  );

  return {
    balance: data?.balance ?? 0,
    balanceUsd: data?.balanceUsd ?? 0,
    bonus: data?.bonus ?? 0,
    wagerRemaining: data?.wagerRemaining ?? 0,
    loading,
    error,
    refetch,
  };
};

/**
 * Hook to poll pending deposits
 * @param {boolean} isLoggedIn - Whether user is logged in
 * @param {number} chainId - Chain ID to filter deposits
 * @returns {Object} { deposits, pendingCount, loading, error, refetch }
 */
export const useDeposits = (isLoggedIn = true, chainId) => {
  const chainParam = chainId ? `?chainId=${chainId}` : '';
  const { data, loading, error, refetch } = usePolling(
    `/user/deposits${chainParam}`,
    5000, // Poll every 5 seconds for pending deposits
    { enabled: isLoggedIn }
  );

  const deposits = data?.deposits ?? [];

  return {
    deposits,
    pendingCount: deposits.filter(d => d.status === 'confirming').length,
    totalDeposited: data?.totalDeposited ?? 0,
    loading,
    error,
    refetch,
  };
};

/**
 * Hook to poll transaction history
 * @param {boolean} isLoggedIn - Whether user is logged in
 * @param {number} limit - Number of transactions to fetch
 * @param {number} chainId - Chain ID to filter transactions
 * @returns {Object} { transactions, loading, error, refetch }
 */
export const useTransactions = (isLoggedIn = true, limit = 10, chainId) => {
  const params = [`limit=${limit}`];
  if (chainId) params.push(`chainId=${chainId}`);
  const { data, loading, error, refetch } = usePolling(
    `/user/transactions?${params.join('&')}`,
    15000, // Poll every 15 seconds
    { enabled: isLoggedIn }
  );

  return {
    transactions: data?.transactions ?? [],
    loading,
    error,
    refetch,
  };
};

/**
 * Hook to poll recent games
 * @param {boolean} isLoggedIn - Whether user is logged in
 * @returns {Object} { games, loading, error, refetch }
 */
export const useRecentGames = (isLoggedIn = true) => {
  const { data, loading, error, refetch } = usePolling(
    '/user/recent-games',
    30000, // Poll every 30 seconds
    { enabled: isLoggedIn }
  );

  return {
    games: data?.games ?? [],
    loading,
    error,
    refetch,
  };
};

/**
 * Hook to fetch the user's unique HD-derived deposit address.
 * Refetches when the selected chain changes.
 * @param {boolean} isLoggedIn - Whether user is logged in
 * @param {number} chainId - Chain ID to fetch deposit address for
 * @returns {Object} { address, chainName, network, minDeposit, confirmationsRequired, loading, error }
 */
export const useDepositAddress = (isLoggedIn = true, chainId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAddress = useCallback(async () => {
    if (!isLoggedIn || !chainId) return;
    setLoading(true);
    try {
      const result = await apiGet(`/user/deposit-address?chainId=${chainId}`);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load deposit address');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, chainId]);

  useEffect(() => {
    fetchAddress();
  }, [fetchAddress]);

  return {
    address: data?.address || null,
    chainName: data?.chainName,
    network: data?.network,
    minDeposit: data?.minDeposit,
    confirmationsRequired: data?.confirmationsRequired,
    loading,
    error,
    refetch: fetchAddress,
  };
};

/**
 * Hook to fetch user profile (no polling, just initial fetch)
 * @param {boolean} isLoggedIn - Whether user is logged in
 * @returns {Object} { user, loading, error, refetch }
 */
export const useUser = (isLoggedIn = true) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUser = useCallback(async () => {
    if (!isLoggedIn) return;

    try {
      setLoading(true);
      const data = await apiGet('/user/profile');
      setUser(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return { user, loading, error, refetch: fetchUser };
};

export default usePolling;
